import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react'
import { Switch, Route, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { PostHogProvider } from 'posthog-js/react'
import { ModalComponent } from './components/modalComponent/modalComponent'

// Lazy loaded pages
const Competitions = lazy(() => import('@src/pages/Competitions').then((module) => ({ default: module.Competitions })))
const SettingsComponent = lazy(() =>
  import('./components/SettingsComponent/SettingsComponent').then((module) => ({ default: module.SettingsComponent }))
)
const SignInComponent = lazy(() =>
  import('./components/SignInComponent').then((module) => ({ default: module.SignInComponent }))
)
const LogoutComponent = lazy(() =>
  import('./components/authentication/LogoutComponent').then((module) => ({ default: module.LogoutComponent }))
)
const LandingPageComponent = lazy(() =>
  import('./components/landingComponent/LandingComponent').then((module) => ({ default: module.LandingPageComponent }))
)
const ConfirmEmailComponent = lazy(() =>
  import('./components/authentication/ConfirmEmailComponent').then((module) => ({
    default: module.ConfirmEmailComponent,
  }))
)
const ResponsibleGaming = lazy(() =>
  import('./pages/ResponsibleGaming').then((module) => ({ default: module.ResponsibleGaming }))
)
const KYCInfo = lazy(() => import('./pages/KYCInfo').then((module) => ({ default: module.KYCInfo })))
const ProfilePage = lazy(() => import('./pages/Profile').then((module) => ({ default: module.ProfilePage })))
const TOS = lazy(() => import('./pages/TOS').then((module) => ({ default: module.TOS })))
const Providers = lazy(() => import('./pages/Providers/Providers').then((module) => ({ default: module.default })))
const Tournament = lazy(() =>
  import('./components/Tournament/Tournament').then((module) => ({ default: module.Tournament }))
)
const Casino = lazy(() => import('./pages/Casino').then((module) => ({ default: module.Casino })))
const Game = lazy(() => import('./components/Game').then((module) => ({ default: module.Game })))
const EliteSeatPage = lazy(() =>
  import('./pages/EliteSeatPage/EliteSeatPage').then((module) => ({ default: module.EliteSeatPage }))
)
const BJFTokenComingSoon = lazy(() =>
  import('./components/BJFToken/BJFToken').then((module) => ({ default: module.BJFTokenComingSoon }))
)
const Healthcheck = lazy(() =>
  import('./pages/Healthcheck/Healthcheck').then((module) => ({ default: module.default }))
)
const BlogPost = lazy(() =>
  import('./pages/BlogPost/BlogPost').then((module) => ({ default: module.BlogPost }))
)
const NotFoundComponent = lazy(() =>
  import('./components/NotFound').then((module) => ({ default: module.NotFoundComponent }))
)

// Lazy loaded components
const Chat = lazy(() => import('./components/chat/Chat').then((module) => ({ default: module.Chat })))
const AuthenticationModal = lazy(() =>
  import('./components/authentication/Authentication/Modal').then((module) => ({ default: module.AuthenticationModal }))
)
const Search2 = lazy(() => import('./components/SearchV2/Search').then((module) => ({ default: module.default })))
const RestrictedAreaModal = lazy(() =>
  import('./components/RestrictedAreaModal/RestrictedAreaModal').then((module) => ({ default: module.default }))
)
const Notifications = lazy(() =>
  import('./components/Notifications').then((module) => ({ default: module.Notifications }))
)

import './App.scss'
import './app.scoped.scss'
import { PrivateRoute } from './components/PrivateRoute'

import Cookie from 'js-cookie'
import { store, IStore } from './store'
import { fetchQuery, requestSubscription, useMutation } from 'react-relay'
import Env from './RelayEnvironment'
import { graphql } from 'react-relay'

import { ReplaySubject, Subject } from 'rxjs'
import { UserBet } from './components/LatestBets/LatestBets'
import { AppGameClosedSubscription } from './__generated__/AppGameClosedSubscription.graphql'
import ConfigurationService from './service/ConfigurationService'
import { ConfigurationServiceGlobalSettingsUpdatedSubscription } from './service/__generated__/ConfigurationServiceGlobalSettingsUpdatedSubscription.graphql'
import { initBets, reloadBets } from './reducers/bet.reducer'
import { CompType, Currency } from './__generated__/AppComponentlistCompetitionsQuery.graphql'
import { AppComponentServerTimeTickSubscription } from './__generated__/AppComponentServerTimeTickSubscription.graphql'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import useIsMobile from './hooks/IsMobile'
import AppTop from './components/AppTop/AppTop'
import classNames from 'classnames'
import Toaster from './components/Toaster/Toaster'
import { AppTypes } from './reducers/app.reducer'
import Modal from './components/Modal/Modal'
import ReactGA from 'react-ga4'
import Toggle2FA from './components/authentication/Toggle2FA/Toggle2FA'
import AccountCreated from './components/authentication/AccountCreated/AccountCreated'
import { useLockScroll } from './hooks/useLockScroll'
import { AppContent } from './containers/AppContent'
import { AppComponentUserUpdatedSubscription } from './__generated__/AppComponentUserUpdatedSubscription.graphql'
import { AppComponentBroadcastCreatedSubscription } from './__generated__/AppComponentBroadcastCreatedSubscription.graphql'
import Login from './components/authentication/Login/Login'

import { useLogin } from './hooks/init/useLogin'
import { LoyaltyReward } from './components/LoyaltyReward'
import {
  changeSubTab,
  changeTab,
  getProfileSubTabs,
  ProfileSubTab,
  ProfileTab,
  profileTabs,
} from './reducers/profile.reducer'
import { open } from './reducers/authModal.reducer'
import { CompetitionsProvider } from './context/CompetitionsContext'
import { allWalletQuery, getFiatRatesQuery } from './components/rightSide/walletComponent/WalletComponent'
import { WalletComponentAllWalletsQuery } from './components/rightSide/walletComponent/__generated__/WalletComponentAllWalletsQuery.graphql'
import { WalletConstant } from './reducers/wallet.reducer'
import AppNavigation from './components/AppNavigation'
import {
  AppComponentDevChangeUserStatValueMutation,
  AppComponentDevChangeUserStatValueMutation$data,
  UserStatType,
} from './__generated__/AppComponentDevChangeUserStatValueMutation.graphql'
import {
  AppComponentDevChangeBalanceMutation,
  AppComponentDevChangeBalanceMutation$data,
} from './__generated__/AppComponentDevChangeBalanceMutation.graphql'
import { useTelegramRedirect } from './hooks/telegram'
import {
  AppComponentDevAddAffiliatePaymentMutation,
  AppComponentDevAddAffiliatePaymentMutation$data,
} from './__generated__/AppComponentDevAddAffiliatePaymentMutation.graphql'
import { getIsSportsBet } from './App.utils'
import { AppBody, GlobalStyles } from './App.styled'
import { globalSettingsUpdatedSubscription } from './service/ConfigurationService.gql'
import { BountiesProvider } from './context/BountiesContext'

import { UserProp, useUserOrigin } from '@src/stores/auth/user'
import { useAuthState, useAuthUser } from '@src/stores/auth'
import { getUserPreference } from '@src/stores/auth/preferences'
import { ThemeProvider, Themes } from '@blackjackfun/ui-components'
import { IntercomProvider } from 'react-use-intercom'
import {
  WalletComponentFiatRatesQuery,
  WalletComponentFiatRatesQuery$data,
} from './components/rightSide/walletComponent/__generated__/WalletComponentFiatRatesQuery.graphql'
import { ProvidersNormalizedGamesQuery$data } from './pages/Providers/__generated__/ProvidersNormalizedGamesQuery.graphql'
import { Spinner } from './components/spinner'
import { getOnRampToast } from './components/Elements/OnRamp/OnRamp.utils'
import { useTranslation } from 'react-i18next'
import { BonusPage } from '@src/pages/Bonus'

const isDevelopment = [undefined, 'development'].includes(process.env.NODE_ENV)
if (!isDevelopment) {
  ReactGA.initialize('G-R5Q55NTYPP')
}

interface GlobalBJ {
  changeUserStateValue: (
    userStat: UserStatType,
    value: number
  ) => Promise<AppComponentDevChangeUserStatValueMutation$data['devChangeUserStatValue']>

  changeBalance: (
    currency: Currency,
    balance: number,
    virtualId?: number
  ) => Promise<AppComponentDevChangeBalanceMutation$data['devChangeBalance']>

  addAffiliatePayment: (
    amount: number,
    currency: Currency,
    fromUserId: number
  ) => Promise<AppComponentDevAddAffiliatePaymentMutation$data['devAddAffiliatePayment']>
}

declare global {
  interface Window {
    bj: GlobalBJ
  }
}

window.bj = window.bj || {}

export type LastBet = {
  bets: {
    biggestWins: Array<UserBet>
    biggestWinsGlobal: Array<UserBet>
    recent: Array<UserBet>
    highrollers: ReadonlyArray<UserBet>
  } | null
}

export type BetStatistic = {
  biggestWins: ReadonlyArray<UserBet>
  biggestWinsGlobal: ReadonlyArray<UserBet>
  recent: ReadonlyArray<UserBet>
  highrollers: ReadonlyArray<UserBet>
}

export type Player = {
  user: {
    uuid: string
    name: string
    hasProfileImage: boolean
  }
  amount: number
  bet: {
    multiplier: number
    normalizedGame: ProvidersNormalizedGamesQuery$data['listNormalizedGames']['entries'][number]
    bets: number[]
    win: number
    btcWin: number
    currency: Currency
  }
}

export type CompetitionRound = {
  roundId: number
  startTime: number
  endTime: number
  payoutsDone: boolean
  payouts: {
    price: number
    votingFreerollCount: number | null
    votingFreerollGame: string | null
    payoutStr: string
    payouts: ReadonlyArray<{
      user: {
        uuid: string
        name: string
      }
      value: string | null
    }>
    votingPayouts: ReadonlyArray<{
      user: {
        uuid: string
        name: string
      }
      value: string | null
    }> | null
  } | null
  votes: ReadonlyArray<{
    user: {
      uuid: string
      name: string
    }
    votes: ReadonlyArray<{
      user: {
        uuid: string
        name: string
      }
      value: string | null
    }>
  }>
  players: Player[]
}

export interface PayoutEntry {
  amount: number
  type: 'freeroll' | 'credit' | 'fixed_credit' | 'rakeback'
  unit: 'spins' | 'percent'
  game?: string
}

export type Competition = {
  compId: number
  type: CompType
  name: string
  autoPayouts: boolean
  autoRenew: boolean
  enabled: boolean
  votingEnabled: boolean
  chatAnnouncement: boolean
  payoutStr: PayoutEntry[]
  price: number
  currency: Currency
  period: string
  votingFreerollCount: number | null
  votingFreerollGame: string | null
  insertedAt: string
  payoutToComp: boolean
  position: number
  runningRound: CompetitionRound | null
}

export let serverTimeDiff = 0
export const errorqueue = new Subject<any>()
export const betStatisticQueue = new ReplaySubject<BetStatistic>(1)
export const chatUserVipUpdated = new Subject<{
  uuid: string
}>()

export let mobile = false

export const setMobile = (isMobile: boolean) => {
  mobile = isMobile
}

export const setTheme = () => {
  const e = document.querySelector('.app-frame')
  if (!e) {
    return
  }
  const bg = getUserPreference(UserProp.BACKGROUND, (value) => value || 'dark')
  if (bg) {
    localStorage.setItem('theme', bg as string)
    e.classList.add('theme-' + bg)
  } else {
    e.classList.add('theme-dark')
  }
}

export const removeTheme = () => {
  const cl = document.querySelector('.app-frame')?.classList
  if (!cl) {
    return
  }
  const toRemove: string[] = []
  for (let i = 0; i < cl.length; i++) {
    const item = cl.item(i)
    if (item && item.startsWith('theme-')) {
      toRemove.push(item)
    }
  }
  toRemove.forEach((x) => cl.remove(x))
}

export const changeChatVisibility = new Subject<boolean>()
export const showVipBar = new Subject<{ uuid: string } | undefined>()

const setModalView = (view: React.ReactElement, onClose: (() => void) | null = null) => {
  store.dispatch({
    type: AppTypes.SET_MODAL_VIEW,
    modal: { view: view, onClose: onClose ?? (() => {}) },
  })
}

function App() {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const authState = useAuthState()
  const user = useAuthUser()
  const origin = useUserOrigin()
  const isRestricted = !!origin?.isBlocked
  useLogin()

  const isSportsBet = getIsSportsBet()
  const [vipUser, setVipUser] = useState<string>()
  const [showRestrictionModal, setShowRestrictionModal] = useState(false)
  const collapsedMenu = useSelector((state: IStore) => state.app.collapsedMenu)
  const chatOpened = useSelector((state: IStore) => state.app.chatOpened)
  const authModalOpened = useSelector((state: IStore) => state.authModal.isOpen)
  const searchOpened = useSelector((state: IStore) => state.app.searchOpened)
  const notificationsOpened = useSelector((state: IStore) => state.app.isNotificationsOpen)

  const [devChangeUserStatValue] = useMutation<AppComponentDevChangeUserStatValueMutation>(
    devChangeUserStateValueMutation
  )
  const [devChangeBalance] = useMutation<AppComponentDevChangeBalanceMutation>(devChangeBalanceMutation)

  useEffect(() => {
    if (isRestricted && origin) {
      setShowRestrictionModal(true)
    }
  }, [isRestricted, origin])

  const handleCloseRestrictionModal = () => {
    setShowRestrictionModal(false)
  }

  useTelegramRedirect()
  const [devAddAffiliatePayment] =
    useMutation<AppComponentDevAddAffiliatePaymentMutation>(devAddAffiliatePaymentMutation)

  const pickWalletWithBalance = () => {
    localStorage.removeItem('pick_wallet_with_balance')

    // check if active currency selected in user preferences
    if (getUserPreference(UserProp.ACTIVE_COIN, (value) => value || false)) return

    fetchQuery<WalletComponentAllWalletsQuery>(Env, allWalletQuery, {}, { fetchPolicy: 'network-only' }).subscribe({
      error: (error: Error) => {
        console.error('Error setting wallet', error)
      },
      next: (data) => {
        if (data.allWallets?.length) {
          const initialWallet = data.allWallets.find((wallet) => wallet != null) ?? { balance: 0 }

          const coinWithMostValue = data.allWallets.reduce((max, coin) => {
            return coin && max && coin.balance > max.balance ? coin : max
          }, initialWallet)

          store.dispatch({
            type: WalletConstant.SET_ACTIVE_WALLET,
            wallet: coinWithMostValue,
          })
        }
      },
    })
  }

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return

    const pick_wallet_with_balance = localStorage.getItem('pick_wallet_with_balance')
    if (!user || !pick_wallet_with_balance) return

    pickWalletWithBalance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authState.type])

  useLockScroll(isMobile && (chatOpened || false))
  useEffect(() => {
    if (chatOpened) {
      document.getElementById('chat')?.focus()
    }
  }, [chatOpened])

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return

    initBets()
    reloadBets()
  }, [authState.type])

  useEffect(() => {
    const getFiatRates = () => {
      fetchQuery<WalletComponentFiatRatesQuery>(Env, getFiatRatesQuery, {}).subscribe({
        next: (data) => {
          parseFiatRates(data)
        },
      })
    }

    getFiatRates()
  }, [])

  const parseFiatRates = (data: WalletComponentFiatRatesQuery$data) => {
    const rates = data.fiatRates.map((x) => {
      return { key: x.key as string, value: +(x.value as string) }
    })
    store.dispatch({ type: WalletConstant.SET_FIAT_RATES, rates })
  }

  useEffect(() => {
    const show_2fa_popup = localStorage.getItem('show_2fa_popup')
    const show_welcome_popup = localStorage.getItem('show_welcome_popup')
    const show_email_popup = localStorage.getItem('show_email_popup')
    const show_email_verified_popup = localStorage.getItem('show_email_verified_popup')

    if (show_2fa_popup) {
      localStorage.removeItem('show_2fa_popup')
      setModalView(<Toggle2FA />, () => {
        if (show_welcome_popup) {
          localStorage.removeItem('show_welcome_popup')
          setModalView(<AccountCreated />)
        }
      })
    } else if (show_welcome_popup) {
      localStorage.removeItem('show_welcome_popup')
      setModalView(<AccountCreated />)
    } else if (show_email_popup) {
      localStorage.removeItem('show_email_popup')
      dispatch(open({ tab: 'Verify' }))
    } else if (show_email_verified_popup) {
      localStorage.removeItem('show_email_verified_popup')
      dispatch(open({ tab: 'Verify Success' }))
    }
  }, [])

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return

    const vipSubs = showVipBar.subscribe((user: any) => {
      if (undefined === user || vipUser === user.uuid) {
        setTimeout(() => {
          setVipUser(undefined)
        }, 200)
        document.querySelector('.vip-bar')?.classList.add('slide-close')
        return
      }
      setVipUser(user.uuid)
    })

    const globalSettingsUpdated = requestSubscription<ConfigurationServiceGlobalSettingsUpdatedSubscription>(Env, {
      subscription: globalSettingsUpdatedSubscription,
      variables: {},
      onNext: (data) => {
        if (data) {
          ConfigurationService.instance.updateFromResponse(data)
        }
      },
    })

    return () => {
      vipSubs.unsubscribe()
      globalSettingsUpdated.dispose()
    }
  }, [authState.type, vipUser])

  const history = useHistory()
  const token = localStorage.getItem('token')

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return
    history.listen(() => {
      ReactGA.send('pageview')
    })

    if (token) {
      Cookie.set('bjfun-auth-token', token, {
        domain: `.blackjack.fun`,
        expires: 7,
      })
    } else {
      Cookie.remove('bjfun-auth-token', {
        domain: `.blackjack.fun`,
      })
    }

    const subscription$ = requestSubscription<AppGameClosedSubscription>(Env, {
      subscription: gameClosedSubscription,
      variables: {},
      onNext: () => {
        history.push('/')
      },
    })

    return () => {
      subscription$.dispose()
    }
  }, [token, history, authState.type])

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return
    const subscription$ = requestSubscription<AppComponentServerTimeTickSubscription>(Env, {
      subscription: serverTimeSubscription,
      variables: {},
      onNext: (time) => {
        if (!time?.serverTimeTick) {
          return
        }
        const ourTime = new Date().getTime()
        const serverTime = time.serverTimeTick
        serverTimeDiff = serverTime - ourTime
      },
    })

    return () => {
      subscription$.dispose()
    }
  }, [authState.type])

  useEffect(() => {
    if (authState.type !== 'LoggedIn') return

    const subscription$ = requestSubscription<AppComponentUserUpdatedSubscription>(Env, {
      subscription: userUpdatedSubscription,
      variables: {},
      onNext: (resp) => {
        console.log(resp)
      },
    })

    return () => {
      subscription$.dispose()
    }
  }, [authState.type])

  useEffect(() => {
    getOnRampToast(t)
  }, [location])

  const { search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  useEffect(() => {
    const subscription$ = requestSubscription<AppComponentBroadcastCreatedSubscription>(Env, {
      subscription: broadcastCreatedSubscription,
      variables: {
        windowId: null,
      },
      onNext: (broadcast) => {
        const broadcastCreated = broadcast?.broadcastCreated as { action?: string; payload?: string }
        if (broadcastCreated?.action === 'WEB_APP_OPEN_SIGNIN') {
          setModalView(<Login />)
        }

        if (broadcastCreated?.action === 'TOURNAMENT_ID_CHANGED') {
          const payload = broadcastCreated.payload
          if (!payload) return

          let url = `/tournament/${payload}`
          if (params.get('track_id')) {
            url += `?track_id=${params.get('track_id')}`
          }

          window.history.pushState({}, '', url)
        }
      },
    })

    return () => {
      subscription$.dispose()
    }
  })

  const dispatch = useDispatch()
  useEffect(() => {
    const urlParams = new URLSearchParams(search)
    const profileTab = urlParams.get('tab')
    const profileTabAllowedValues = profileTabs.map((pt) => pt.name.toLowerCase())

    if (!profileTab || !profileTabAllowedValues.includes(profileTab.toLowerCase())) return
    dispatch(changeTab({ tab: profileTab as ProfileTab }))

    const profileSubTab = urlParams.get('stab')
    if (profileSubTab) {
      dispatch(changeSubTab({ subTab: profileSubTab as ProfileSubTab<ProfileTab> }))
      return
    }

    const subTabs = [...getProfileSubTabs(profileTab as ProfileTab)]
    const firstEnabled = subTabs.find((st) => st.disabled === false)

    if (!firstEnabled) return
    dispatch(changeSubTab({ subTab: firstEnabled.name as ProfileSubTab<ProfileTab> }))
  }, [search, dispatch])

  useEffect(() => {
    const urlParams = new URLSearchParams(search)
    const btag = urlParams.get('btag')

    if (btag && authState.type !== 'LoggedIn') {
      dispatch(open({ tab: 'Join Us' }))
    }
  }, [search, dispatch, authState.type])

  useEffect(() => {
    if (user?.kycState === 'waiting_submission') {
      history.push(`/profile/${user.uuid}?tab=settings&stab=KYC`)
    }
  }, [user, history])

  useEffect(() => {
    window.bj.changeUserStateValue = (userStat: UserStatType, value: number) => {
      return new Promise((resolve, reject) => {
        devChangeUserStatValue({
          variables: {
            input: {
              key: userStat,
              value,
            },
          },
          onCompleted: (data) => {
            resolve(data.devChangeUserStatValue)
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    }

    window.bj.changeBalance = (currency: Currency, balance: number, virtualId?: number) => {
      return new Promise((resolve, reject) => {
        devChangeBalance({
          variables: {
            input: {
              currency,
              balance,
              virtualId: virtualId || 0,
            },
          },
          onCompleted: (data) => {
            resolve(data.devChangeBalance)
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    }
    window.bj.addAffiliatePayment = (amount: number, currency: Currency, fromUserId: number) => {
      return new Promise((resolve, reject) => {
        devAddAffiliatePayment({
          variables: {
            input: {
              amount,
              currency,
              fromUserId,
            },
          },
          onCompleted: (data) => {
            resolve(data.devAddAffiliatePayment)
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    }
  }, [])
  const isBonusPage = Boolean(user && user.featureFlags?.includes('bonuses'));
  const w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
  mobile = w < 1400

  const theme = getUserPreference(UserProp.BACKGROUND, (value) => value || 'dark')

  if (user?.kycState === 'waiting_submission') {
    return (
      <>
        <ProfilePage />
        <ReactTooltip />
      </>
    )
  }

  const postHogOptions = {
    person_profiles: 'always',
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  }

  const ihgPathnames = ['/game/bitcoin-blackjack', '/game/bjfun:plinko', '/game/redorblack']
  const isIHGpage = ihgPathnames.includes(history.location.pathname)

  const isNewIHGPage = ['/game/bjfun:dice', '/game/bjfun:mines', '/game/bjfun:crash', '/game/bjfun:limbo'].includes(
    history.location.pathname
  )

  const getAutoBootProps = () => {
    if (user?.uuid) {
      return {
        userId: user?.uuid,
        name: user?.name || '',
        email: user?.username || '',
        createdAt: user?.insertedAt ? new Date(user.insertedAt).getTime() : undefined,
        hideDefaultLauncher: true,
      }
    }
    return {}
  }

  return (
    <div
      className={classNames(`app-frame theme-${theme}`, {
        chatOpened: chatOpened,
        collapsed: collapsedMenu,
        notificationsOpened: notificationsOpened,
      })}
    >
      {user && (
        <IntercomProvider
          autoBoot
          appId={process.env.REACT_APP_INTERCOM_APP_ID || ''}
          autoBootProps={getAutoBootProps()}
          initializeDelay={1000}
        >
          <PostHogProvider apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY} options={postHogOptions}>
            <GlobalStyles />
            <ThemeProvider chosenTheme={Themes.original}>
              <BountiesProvider>
                <AppTop user={user} />
                <AppNavigation me={user} />
                <AppBody
                  className={classNames(`app-body`, {
                    sport: isSportsBet,
                  })}
                  $isSportsBet={isSportsBet}
                  $ihgWrapper={isIHGpage}
                  $isMobile={isMobile}
                  $isNewIHGPage={isNewIHGPage}
                >
                  <AppContent>
                    <CompetitionsProvider>
                      <Suspense fallback={<Spinner />}>
                        <Switch>
                          <Route path="/signin" component={SignInComponent} />
                          <Route path="/healthcheck" component={Healthcheck} />
                          <Route path="/confirm/:uuid/:secret" component={ConfirmEmailComponent} />
                          <PrivateRoute path="/responsible-gaming" component={ResponsibleGaming} />
                          <PrivateRoute path="/kyc-policy" component={KYCInfo} />
                          <PrivateRoute path="/tos" component={TOS} />
                          <PrivateRoute path="/logout" component={LogoutComponent} />
                          <PrivateRoute path="/profile" component={ProfilePage} />
                          <PrivateRoute path="/settings" component={SettingsComponent} />
                          <PrivateRoute path="/providers" component={Providers} />
                          <PrivateRoute path="/tournament/:tid" component={Tournament} />
                          <PrivateRoute path="/casino" component={Casino} />
                          <PrivateRoute path="/game/:gameId" component={Game} />
                          <PrivateRoute path="/game/:gameId/:subGameId" component={Game} />
                          <PrivateRoute path="/competitions" component={Competitions} />
                          <PrivateRoute path="/elite-seat" component={EliteSeatPage} />
                          <PrivateRoute path="/bjf-token" component={BJFTokenComingSoon} />
                          <PrivateRoute path="/blog/:slug" component={BlogPost} />
                          <PrivateRoute path="/:action/:key" component={LandingPageComponent} />
                          {isBonusPage && <PrivateRoute path="/bonuses" component={BonusPage} />}
                          <PrivateRoute exact path="/" component={LandingPageComponent} />
                          <Route component={NotFoundComponent} />
                        </Switch>
                      </Suspense>
                    </CompetitionsProvider>
                  </AppContent>
                  {notificationsOpened && (
                    <Suspense fallback={<Spinner />}>
                      <Notifications />
                    </Suspense>
                  )}
                  <LoyaltyReward />
                </AppBody>
                <Suspense fallback={<Spinner />}>
                  <Chat />
                </Suspense>
                {authModalOpened && (
                  <Suspense fallback={<Spinner />}>
                    <AuthenticationModal />
                  </Suspense>
                )}
                <ModalComponent me={user} />
                <Modal />
                <ReactTooltip />
                <Toaster />
                {searchOpened && (
                  <Suspense fallback={<Spinner />}>
                    <Search2 />
                  </Suspense>
                )}
                {isRestricted && origin && showRestrictionModal && (
                  <Suspense fallback={<Spinner />}>
                    <RestrictedAreaModal isOpen={true} onClose={handleCloseRestrictionModal} origin={origin} />
                  </Suspense>
                )}
              </BountiesProvider>
            </ThemeProvider>
          </PostHogProvider>
        </IntercomProvider>
      )}
    </div>
  )
}

export default App

const serverTimeSubscription = graphql`
  subscription AppComponentServerTimeTickSubscription {
    serverTimeTick
  }
`

graphql`
  fragment AppCompetitionRound on CompetitionRound {
    roundId: id
    startTime
    endTime
    payoutsDone
    payouts {
      price
      payoutStr
      votingFreerollCount
      votingFreerollGame
      payouts {
        user {
          uuid
          name
        }
        value
      }
      votingPayouts {
        user {
          uuid
          name
        }
        value
      }
    }
    votes {
      user {
        uuid
        name
      }
      votes {
        user {
          uuid
          name
        }
        value
      }
    }
    players {
      user {
        uuid
        name
        hasProfileImage
      }
      amount
      bet {
        multiplier
        bets
        win
        currency
        btcWin
        normalizedGame {
          label
          gameId
          provider
          urlThumb
        }
      }
    }
  }
`
graphql`
  fragment AppCompetitionFragment on Competition {
    compId: id
    type
    name
    autoPayouts
    autoRenew
    enabled
    votingEnabled
    chatAnnouncement
    payoutStr
    price
    currency
    period
    votingFreerollCount
    votingFreerollGame
    insertedAt
    payoutToComp
    position
    runningRound {
      ...AppCompetitionRound @relay(mask: false)
    }
  }
`

export const listCompetitionsQuery = graphql`
  query AppComponentlistCompetitionsQuery {
    listCompetitions {
      ...AppCompetitionFragment @relay(mask: false)
    }
  }
`

export const competitionUpdatedSubscription = graphql`
  subscription AppComponentCompetitionUpdatedSubscription {
    competitionUpdated {
      ...AppCompetitionFragment @relay(mask: false)
    }
  }
`

export const roundEndedSubscription = graphql`
  subscription AppComponentRoundEndedSubscription {
    roundEnded {
      ...AppCompetitionRound @relay(mask: false)
    }
  }
`

export const roundAddedSubscription = graphql`
  subscription AppComponentRoundAddedSubscription {
    roundAdded {
      ...AppCompetitionRound @relay(mask: false)
    }
  }
`

export const roundDeletedSubscription = graphql`
  subscription AppComponentRoundDeletedSubscription {
    roundDeleted {
      ...AppCompetitionRound @relay(mask: false)
    }
  }
`

export const userUpdatedSubscription = graphql`
  subscription AppComponentUserUpdatedSubscription {
    userUpdated {
      preferences {
        key
        value
      }
    }
  }
`

export const betUpdatedSubscription = graphql`
  subscription AppComponentBetUpdatedSubscription {
    betUpdated {
      gameId
      gameProvider
      currency
      virtualId
      finished
      bets
      win
      btcBet
      btcWin
      multiplier
      meta
      insertedAt
      normalizedGame {
        label
        gameId
        provider
      }
      user {
        uuid
        name
        hasProfileImage
      }
    }
  }
`

export const broadcastCreateMutation = graphql`
  mutation AppComponentCreateBroadcastMutation($input: CreateBroadcastInput!) {
    createBroadcast(input: $input) {
      result
    }
  }
`

export const broadcastCreatedSubscription = graphql`
  subscription AppComponentBroadcastCreatedSubscription($windowId: String) {
    broadcastCreated(windowId: $windowId)
  }
`

export const loginTelegramMutation = graphql`
  mutation ApploginTelegramMutation($input: LoginTelegramInput!) {
    loginTelegram(input: $input) {
      token
      user {
        name
        username
        otpEnabled
        anonymousHash
        uuid
        roles
        confirmed
        preferences {
          key
          value
          valueJson
        }
      }
    }
  }
`

export const devChangeUserStateValueMutation = graphql`
  mutation AppComponentDevChangeUserStatValueMutation($input: DevChangeUserStatValueInput!) {
    devChangeUserStatValue(input: $input) {
      user {
        uuid
        userStats {
          name
          value
          meta
        }
      }
    }
  }
`

export const devChangeBalanceMutation = graphql`
  mutation AppComponentDevChangeBalanceMutation($input: DevChangeBalanceInput!) {
    devChangeBalance(input: $input) {
      ok
    }
  }
`

export const devAddAffiliatePaymentMutation = graphql`
  mutation AppComponentDevAddAffiliatePaymentMutation($input: DevAddAffiliatePaymentInput!) {
    devAddAffiliatePayment(input: $input) {
      ok
    }
  }
`

export const gameClosedSubscription = graphql`
  subscription AppGameClosedSubscription {
    sGameClosed
  }
`
